import React from 'react';
import { Pencil } from 'lucide-react';

import { ViewEntityHeader } from 'components/entityViewer/ViewEntityHeader';
import { FCTransferActionsMenu } from 'components/warehouse/components/fcTransfers/FCTransferActionsMenu';
import {
    getTransferNumberDisplay,
    getTransferStatusDisplay,
    isFCTransferPendingStatus,
} from 'components/warehouse/components/fcTransfers/fcTransferDisplayUtils';
import { FC_TRANSFER_DIRECTION } from 'components/warehouse/components/fcTransfers/FCTransferFilterConfig';
import { Badge } from 'shadcn/components/ui/Badge';
import { Button } from 'shadcn/components/ui/Button';

/**
 * FC Transfer entity page header
 *
 * @param {Object} props
 * @param {string | import('react-router-dom').To} props.backHref
 * @param {string} [props.backLabel='Back to FC Transfers']
 * @param {Object} [props.backLinkProps]
 * @param {'OUTBOUND' | 'INBOUND'} [props.direction]
 * @param {Record<string, unknown> | null} [props.transfer]
 * @param {() => void} [props.onEditTransfer]
 * @param {boolean} [props.isLineItemsEditMode]
 * @param {() => void} [props.onCancelLineItemsEdit]
 * @param {() => void} [props.onSaveLineItemsEdit]
 * @param {boolean} [props.isSavingLineItems]
 * @param {boolean} [props.lineItemsSaveDisabled]
 * @param {boolean} [props.canEditOutgoingLineItems] - When `false`, outbound line-item edit is disabled (e.g. user is not at source facility). Omit or non-`false` keeps legacy “pending only” behavior.
 * @param {() => void} [props.onDownloadBol]
 * @param {() => void} [props.onCancelTransfer]
 * @param {() => void} [props.onAccept]
 */
export function FCTransferEntityHeader({
    backHref,
    backLabel = 'Back to FC Transfers',
    backLinkProps,
    canEditOutgoingLineItems,
    direction = FC_TRANSFER_DIRECTION.OUTBOUND,
    isLineItemsEditMode = false,
    isSavingLineItems = false,
    lineItemsSaveDisabled = false,
    onAccept = () => {},
    onCancelLineItemsEdit = () => {},
    onCancelTransfer = () => {},
    onDownloadBol = () => {},
    onEditTransfer = () => {},
    onSaveLineItemsEdit = () => {},
    transfer = null,
}) {
    const transferNumber = getTransferNumberDisplay(transfer);
    const statusLabel = getTransferStatusDisplay(transfer);
    const isEditTransferDisabled = !isFCTransferPendingStatus(transfer) || canEditOutgoingLineItems === false;
    const isInbound = direction === FC_TRANSFER_DIRECTION.INBOUND;
    const showEditTransferButton = !isInbound;

    const isSaveDisabled = isSavingLineItems || lineItemsSaveDisabled;

    const entityIdentificationComponent = (
        <div className="flex w-full min-w-0 flex-wrap items-start gap-3 lg:items-center">
            <div className="flex min-w-0 flex-wrap items-center gap-3">
                <h1 className="text-2xl font-semibold tracking-tight text-foreground">Transfer # {transferNumber}</h1>
                <Badge variant="outline" className="shrink-0 font-normal">
                    {statusLabel}
                </Badge>
            </div>
            <div className="ml-auto flex shrink-0 items-center gap-2">
                {showEditTransferButton && isLineItemsEditMode ? (
                    <>
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            disabled={isSavingLineItems}
                            onClick={onCancelLineItemsEdit}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="button"
                            variant="default"
                            size="sm"
                            disabled={isSaveDisabled}
                            onClick={onSaveLineItemsEdit}
                            title={lineItemsSaveDisabled ? 'Stage line item changes before saving' : undefined}
                        >
                            {isSavingLineItems ? 'Saving…' : 'Save changes'}
                        </Button>
                    </>
                ) : null}
                {showEditTransferButton && !isLineItemsEditMode ? (
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={onEditTransfer}
                        disabled={isEditTransferDisabled}
                        title={
                            canEditOutgoingLineItems === false && isFCTransferPendingStatus(transfer)
                                ? 'Line-item edit is only available when your facility is the source of this transfer.'
                                : undefined
                        }
                    >
                        <Pencil className="mr-2 h-4 w-4" aria-hidden="true" />
                        Edit Transfer
                    </Button>
                ) : null}
                <FCTransferActionsMenu
                    direction={direction}
                    showEditTransferAction={false}
                    isEditTransferDisabled={isEditTransferDisabled}
                    onAccept={onAccept}
                    onCancelTransfer={onCancelTransfer}
                    onDownloadBol={onDownloadBol}
                    onEditTransfer={onEditTransfer}
                />
            </div>
        </div>
    );

    return (
        <ViewEntityHeader
            backHref={backHref}
            backLabel={backLabel}
            backLinkProps={backLinkProps}
            entityIdentificationComponent={entityIdentificationComponent}
        />
    );
}

FCTransferEntityHeader.displayName = 'FCTransferEntityHeader';
